import { escapeHtml } from '../lib/html.js';
import { statusPillClass } from '../lib/status.js';
import { listShopifyProdutos } from '../lib/shopify.js';
import { fmtValor, moedaBase } from '../lib/moeda.js';
import { buildHref } from '../lib/router.js';
import { paginate, CATALOG_PAGE_SIZE } from '../lib/pagination.js';
import { renderPager } from './pager.js';

let produtos = [];
let loaded = false;
let loadedMoeda = '';
let loading = false;
let loadError = '';
const filters = { loja: '', status: '' };

export function setProdutosFilter(name, value) {
  filters[name] = value || '';
}

export function invalidateProdutos() {
  loaded = false;
}

export async function refreshProdutos() {
  loading = true;
  loadError = '';
  try {
    produtos = await listShopifyProdutos({ limit: '500', moeda: moedaBase() });
    loadedMoeda = moedaBase();
    loaded = true;
  } catch (err) {
    loadError = err.message || 'Não foi possível carregar os produtos.';
    produtos = [];
    loaded = true;
    loadedMoeda = moedaBase();
  } finally {
    loading = false;
  }
  return produtos;
}

function money(value, currency = 'BRL') {
  try {
    return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: currency || 'BRL' });
  } catch {
    return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }
}

function priceRange(min, max, currency) {
  if (min == null && max == null) return '—';
  if (min == null) return money(max, currency);
  if (max == null || Number(min) === Number(max)) return money(min, currency);
  return `${money(min, currency)} – ${money(max, currency)}`;
}

function statusLabel(status) {
  const map = { active: 'ativo', draft: 'rascunho', archived: 'arquivado' };
  return map[String(status || '').toLowerCase()] || status || '—';
}

function filtered() {
  return produtos.filter((item) => {
    if (filters.loja && item.shop_domain !== filters.loja) return false;
    if (filters.status && String(item.status || '').toLowerCase() !== filters.status) return false;
    return true;
  });
}

export function renderProdutos({ page = 1, onNeedRefresh } = {}) {
  if (loaded && loadedMoeda !== moedaBase()) loaded = false;
  if (!loaded && !loading) {
    refreshProdutos().then(() => onNeedRefresh?.());
  }
  if (!loaded || loading) {
    return `<div class="empty-state">Carregando produtos…</div>`;
  }
  if (loadError) {
    return `<div class="empty-state">${escapeHtml(loadError)}</div>`;
  }

  const list = filtered();
  const pg = paginate(list, page, CATALOG_PAGE_SIZE);
  const lojas = [...new Set(produtos.map((item) => item.shop_domain).filter(Boolean))].sort();
  const ativos = list.filter((item) => String(item.status || '').toLowerCase() === 'active').length;
  const comVenda = list.filter((item) => Number(item.vendas?.unidades || 0) > 0).length;
  const vendido = list.reduce((sum, item) => sum + (Number(item.vendas?.vendido) || 0), 0);
  const unidades = list.reduce((sum, item) => sum + (Number(item.vendas?.unidades_pagas) || 0), 0);

  const rows = pg.total
    ? pg.items.map((produto) => {
        const sales = produto.vendas || {};
        const thumb = produto.image_url
          ? `<img class="cell-thumb" src="${escapeHtml(produto.image_url)}" alt="">`
          : `<span class="cell-thumb" aria-hidden="true"></span>`;
        return `<tr>
          <td>
            <div class="cell-with-thumb">
              ${thumb}
              <div>
                <div class="cell-title">${escapeHtml(produto.title || 'Sem título')}</div>
                <div class="cell-sub">${escapeHtml(produto.sku || produto.handle || 'sem SKU')} · ${escapeHtml(produto.loja_nome || produto.shop_domain)}</div>
              </div>
            </div>
          </td>
          <td>
            <span class="pill ${statusPillClass(statusLabel(produto.status))}">${escapeHtml(statusLabel(produto.status))}</span>
            <div class="cell-sub">${escapeHtml(produto.product_type || produto.vendor || '—')}</div>
          </td>
          <td>
            <div class="cell-title">${escapeHtml(priceRange(produto.price_min, produto.price_max, produto.currency))}</div>
            <div class="cell-sub">${produto.variants_count || 0} var. · est. ${produto.inventory ?? 0} · ${escapeHtml(produto.currency || 'BRL')}</div>
          </td>
          <td>
            <div class="cell-title">${sales.unidades_pagas || 0} un. pagas</div>
            <div class="cell-sub">${sales.pagos || 0} pedido(s) · ${sales.unidades || 0} un. no total</div>
          </td>
          <td>
            <div class="cell-title">${escapeHtml(fmtValor(sales.vendido || 0))}</div>
            <div class="cell-sub">${escapeHtml(fmtValor(sales.bruto || 0))} bruto</div>
          </td>
        </tr>`;
      }).join('')
    : `<tr><td colspan="5" class="empty-state">Nenhum produto importado. Conecte a loja e sincronize o catálogo.</td></tr>`;

  return `
    <div class="fat-stats" style="margin-bottom:16px;flex-wrap:wrap">
      <div class="mini-stat">
        <div class="lbl">Produtos</div>
        <div class="val">${list.length}</div>
        <div class="cell-sub">${ativos} ativo(s)</div>
      </div>
      <div class="mini-stat">
        <div class="lbl">Com venda</div>
        <div class="val">${comVenda}</div>
        <div class="cell-sub">${unidades} un. pagas</div>
      </div>
      <div class="mini-stat">
        <div class="lbl">Vendido pago (${moedaBase()})</div>
        <div class="val">${escapeHtml(fmtValor(vendido))}</div>
        <div class="cell-sub">itens do pedido convertidos</div>
      </div>
    </div>
    <div class="table-toolbar">
      <div style="display:flex;gap:8px;flex-wrap:wrap">
        <select class="filter" id="produtos-loja">
          <option value="">Todas as lojas</option>
          ${lojas.map((loja) => `<option value="${escapeHtml(loja)}" ${filters.loja === loja ? 'selected' : ''}>${escapeHtml(loja)}</option>`).join('')}
        </select>
        <select class="filter" id="produtos-status">
          <option value="" ${!filters.status ? 'selected' : ''}>Todos os status</option>
          <option value="active" ${filters.status === 'active' ? 'selected' : ''}>Ativos</option>
          <option value="draft" ${filters.status === 'draft' ? 'selected' : ''}>Rascunho</option>
          <option value="archived" ${filters.status === 'archived' ? 'selected' : ''}>Arquivados</option>
        </select>
      </div>
      <span class="cell-sub">${pg.total} produto(s)</span>
    </div>
    <div class="panel-box"><div class="table-scroll"><table>
      <thead><tr><th>Produto</th><th>Status</th><th>Preço / estoque</th><th>Vendas</th><th>Faturamento</th></tr></thead>
      <tbody>${rows}</tbody>
    </table></div></div>
    ${pg.total ? renderPager({
      id: 'produtos',
      page: pg.page,
      totalPages: pg.totalPages,
      from: pg.from,
      to: pg.to,
      total: pg.total,
      label: 'produto(s)',
      hrefFor: (n) => buildHref({ section: 'produtos', page: n }),
    }) : ''}`;
}
